import { isUsLocale } from '@shared/locale/locale';
import { AddressPresenter } from '@shared/modules/addresses/address.presenter';
import { BookingTaskAllocationPresenter } from '@shared/modules/bookings/booking-task-allocation.presenter';
import { BookingStatus } from '@shared/modules/bookings/models/booking-status';
import type { Carer } from '@shared/modules/carers/carer';
import { CheckInPresenter } from '@shared/modules/check-ins';
import { FixedRatePresenter } from '@shared/modules/fixed-rates/fixed-rate.presenter';
import { ServiceContractPresenter } from '@shared/modules/service-contracts/service-contract.presenter';
import { ReconcilableTaskPresenter } from '@shared/modules/tasks/reconcilable-task.presenter';
import { UserPresenter } from '@shared/modules/users/user.presenter';
import { DiaryEventPresenter } from '@shared/presenters/diary-event.presenter';
import { isAnyPresent, isPresent } from '@shared/utils/helpers';
import type { Warning } from '@shared/utils/warnings';
import { DateTime } from 'luxon';
import { niceList } from '../../utils/strings';
import type { BookingType } from '../booking-types/booking-type';
import { BookingTypePresenter } from '../booking-types/booking-type-presenter';
import { CarerPresenter } from '../carers/carer.presenter';
import { ClientPresenter } from '../clients/client.presenter';
import type { Team } from '../teams/team';
import { TeamPresenter } from '../teams/team-presenter';
import type { Booking } from './models/booking';

export class BookingPresenter extends DiaryEventPresenter<Booking> {
    get client(): ClientPresenter {
        return this.present('client.data', ClientPresenter);
    }

    get checkIns(): CheckInPresenter[] {
        return this.presentCollection('check_ins.data', CheckInPresenter);
    }

    get tasks(): BookingTaskAllocationPresenter[] {
        return this.presentCollection('tasks.data', BookingTaskAllocationPresenter);
    }

    get reconcilableTasks(): ReconcilableTaskPresenter[] {
        return this.presentCollection('reconcilable_tasks.data', ReconcilableTaskPresenter);
    }

    get unplannedReconciledBy(): UserPresenter {
        return this.present('unplanned_reconciled_by.data', UserPresenter);
    }

    get publishedBy(): UserPresenter {
        return this.present('published_by.data', UserPresenter);
    }

    get unplannedReconciledAt(): string {
        return this._get('unplannedReconciledAt', () =>
            this.formatTimestamp(this.toDateTime('unplanned_reconciled_at')),
        );
    }

    get teams(): Team[] {
        return this.presentAttach('teams.data', TeamPresenter);
    }

    get type(): BookingTypePresenter {
        return this.present<BookingType>('type.data', BookingTypePresenter);
    }

    get startDateTime() {
        if (isUsLocale()) {
            return this.toDateTime('start').toFormat('L/d hh:mm');
        }

        return this.toDateTime('start').toFormat('dd/LL hh:mm');
    }

    get startDateRelative() {
        return this._get('startDateRelative', () => this.formatDateReadable(this.start));
    }

    get hasBeenMoved(): boolean {
        return this._get(
            'hasBeenMoved',
            () => this.data.original_start !== this.data.start || this.data.original_duration !== this.data.duration,
        );
    }

    get originalStart(): DateTime {
        return this.toDateTime('original_start');
    }

    get originalDuration(): number {
        return this.data.original_duration;
    }

    get movedBySeconds() {
        return this._get('movedBy', () => this.start.diff(this.originalStart, 'seconds').seconds);
    }

    get minStart(): DateTime {
        return this._get('minStart', () => {
            const before = this._data.restrict_start_minutes_before;

            if (isPresent(before)) {
                const originalStart = this.originalStart;

                if (originalStart) {
                    return originalStart.minus({ minutes: before });
                }
            }

            return undefined;
        });
    }

    get minStartTime(): string {
        return this._get('minStartTime', () => this.formatTime(this.minStart));
    }

    get maxStart(): DateTime {
        return this._get('maxStart', () => {
            const after = this._data.restrict_start_minutes_after;

            if (isPresent(after)) {
                const originalStart = this.originalStart;

                if (originalStart) {
                    return originalStart.plus({ minutes: after });
                }
            }

            return undefined;
        });
    }

    get maxStartTime(): string {
        return this._get('maxStartTime', () => this.formatTime(this.maxStart));
    }

    get teamNames() {
        return this._get('teamNames', () => niceList((this.teams || []).map(team => team.description)));
    }

    get publishedAt() {
        return this.toDateTime('published_at', true);
    }

    get isPublished() {
        return this._get('isPublished', () => {
            const publishedAt = this.publishedAt;

            if (publishedAt) {
                return publishedAt.valueOf() >= new Date().valueOf();
            }

            return false;
        });
    }

    get carers(): CarerPresenter[] {
        return this.present('carers.data', CarerPresenter)?.sort((a, b) => a.name > b.name);
    }

    /**
     * The names of the carers assigned to this booking. An array is returning as it is
     * more flexible for formatting.
     *
     * @return
     */
    get carerNames(): string[] {
        return this._get('carerNames', () =>
            this.carers.map(carer => carer.person.nameNormal).sort((a, b) => (a > b ? 1 : -1)),
        );
    }

    get address(): AddressPresenter {
        return this.present('address.data', AddressPresenter);
    }

    get isCancelled() {
        return this.data.is_cancelled;
    }

    get cancelled() {
        return this.data.is_cancelled;
    }

    get cancelledBy(): UserPresenter {
        return this.present('cancelled_by.data', UserPresenter);
    }

    get visited() {
        return this.data.has_been_visited;
    }

    get hasTimeRestrictions(): boolean {
        return isAnyPresent(
            this.data.restrict_start_minutes_before,
            this.data.restrict_start_minutes_after,
            this.data.restrict_duration_min,
            this.data.restrict_duration_max,
        );
    }

    get serviceContract(): ServiceContractPresenter {
        return this.present('service_contract.data', ServiceContractPresenter);
    }

    get serviceContractFixedRate(): FixedRatePresenter {
        return this.present('service_contract_fixed_rate.data', FixedRatePresenter);
    }

    get title(): string {
        return this._get('title', () => this.client?.person?.nameLastFirst ?? this.type?.description ?? 'Booking');
    }

    get subtitle(): string {
        return this._get('subtitle', () => this.type?.description || 'Booking');
    }

    get typeAndTime(): string {
        return this._get('typeAndTime', () => `${this.subtitle} @ ${this.startTimestamp}`);
    }

    get cancelledAt() {
        return this.toDateTime('cancelled_at');
    }

    get cancelledAtReadable() {
        return this._get('cancelledAtReadable', () => this.formatDateTimeReadable(this.cancelledAt));
    }

    get readableCarerNames() {
        return niceList(this.carerNames);
    }

    get isAllocated() {
        return this._get('isAllocated', () => this.has('carers.data') && this.get('carers.data', []).length > 0);
    }

    get isUnallocated() {
        return !this.isAllocated;
    }

    get isMissingCarers() {
        return this._get('isMissingCarers', () => {
            const minCarers = this.get('additional_attributes.min_carers');

            if (!minCarers) {
                return false;
            }

            if (!this.has('carers.data')) {
                // Impossible to tell without the carers array. Realistically this method will only be used
                // where it has been included.
                return false;
            }

            return this.get('carers.data', []).length < minCarers;
        });
    }

    /*
     * Restrictions
     */

    get canAllocateCarers() {
        return this.get('restrictions.data.can_allocate_carers');
    }

    get canUnallocateCarers() {
        return this.get('restrictions.data.can_unallocate_carers');
    }

    get canUpdateCarerContract() {
        return this.get('restrictions.data.can_update_carer_contract');
    }

    get canUpdateTime(): boolean {
        return this.get('restrictions.data.can_update_time');
    }

    get cannotUpdateTime() {
        return !this.canUpdateTime;
    }

    get canUpdateClient() {
        return this.get('restrictions.data.can_update_client');
    }

    get canUpdateAddress() {
        return this.get('restrictions.data.can_update_address');
    }

    get cannotUpdateAddress() {
        return !this.canUpdateAddress;
    }

    get canUpdateType() {
        return this.get('restrictions.data.can_update_type');
    }

    get cannotUpdateType() {
        return !this.canUpdateType;
    }

    get canAddTasks() {
        return !this.get('has_been_visited');
    }

    get canCancel() {
        return this.get('restrictions.data.can_cancel');
    }

    get cannotCancel() {
        return !this.canCancel;
    }

    get canUncancel() {
        return this.get('restrictions.data.can_uncancel');
    }

    get cannotUncancel() {
        return !this.canUncancel;
    }

    get canConfirm() {
        return this.get('restrictions.data.can_confirm') === true;
    }

    get cannotConfirm() {
        return !this.canConfirm;
    }

    get canUnconfirm() {
        return this.get('restrictions.data.can_unconfirm');
    }

    get cannotUnconfirm() {
        return !this.canUnconfirm;
    }

    get canDelete() {
        return this.get('restrictions.data.can_delete');
    }

    get cannotDelete() {
        return !this.canDelete;
    }

    get canAddCheckIns() {
        return this.get('restrictions.data.can_add_check_ins');
    }

    get mustCheckIn() {
        return this._get('mustCheckIn', () => this.data.type.data?.must_check_in);
    }

    get cannotAddCheckIns() {
        return !this.canAddCheckIns;
    }

    get canOverlapClient(): boolean {
        return this.get('additional_attributes.can_overlap_client', false);
    }

    get hasOverlappingEnabled(): boolean {
        return (
            this.data.additional_attributes?.can_overlap_client || this.data.additional_attributes?.can_overlap_carers
        );
    }

    /*
    Grid
     */

    get statusEnum(): BookingStatus {
        return this._get<BookingStatus>('statusEnum', () => {
            if (this.data.confirmation.data.is_confirmed) {
                return BookingStatus.confirmed;
            } else if (this.data.is_cancelled) {
                return BookingStatus.cancelled;
            } else if (this.data.has_been_visited) {
                return BookingStatus.visited;
            } else if (this.data.has_visit_started) {
                return BookingStatus.visit_started;
            } else if (this.isUnallocated) {
                return BookingStatus.unallocated;
            } else if (this.isMissingCarers) {
                return BookingStatus.partially_unallocated;
            }

            return null;
        });
    }

    /**
     * @return
     */
    get gridCssClass() {
        return this._get('gridCssClass', () => {
            if (this.data.confirmation.data.is_confirmed) {
                return 'booking-confirmed';
            } else if (this.data.is_cancelled) {
                return 'booking-cancelled';
            } else if (this.data.has_been_visited) {
                return 'booking-visited';
            } else if (this.isUnallocated) {
                return 'booking-unallocated';
            } else if (this.isMissingCarers) {
                return 'booking-partially-unallocated';
            }
        });
    }

    confirmationWarningsForCarer(carer: Carer): Warning[] {
        if (!carer) {
            return null;
        }

        return this._get('confirmationWarningsForCarer' + carer.id, () => {
            const warnings = this.data.confirmation_warnings?.data ?? [];

            return warnings.filter(warning => warning.person_id === carer.person_id);
        });
    }

    get unlockedUntil(): DateTime {
        return this.toDateTime('unlocked_until');
    }

    get isUnlocked() {
        const now = DateTime.local();

        return this._get(`isUnlocked:${now.startOf('minute').toMillis()}`, () => {
            const instance = this.unlockedUntil;

            if (!instance) {
                return false;
            }

            return instance >= DateTime.local();
        });
    }
}
